import { Head, Link, router } from "@inertiajs/react"
import {
  ArrowLeft,
  ExternalLink,
  AlertCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
} from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

// Color by pipeline name pattern: article=amber, main=blue, referral=emerald, else=purple
function getPipelineColors(name) {
  const n = (name || "").toLowerCase()
  if (n.includes("article")) return { badge: "bg-amber-100 text-amber-800 hover:bg-amber-100", card: "border-amber-200 bg-amber-50/30" }
  if (n.includes("main")) return { badge: "bg-blue-100 text-blue-800 hover:bg-blue-100", card: "border-blue-200 bg-blue-50/30" }
  if (n.includes("referral")) return { badge: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100", card: "border-emerald-200 bg-emerald-50/30" }
  return { badge: "bg-purple-100 text-purple-800 hover:bg-purple-100", card: "border-purple-200 bg-purple-50/30" }
}

function timeInStageColor(days) {
  if (days == null) return "text-zinc-400"
  if (days >= 30) return "text-red-600 font-semibold"
  if (days >= 14) return "text-amber-600 font-medium"
  return "text-zinc-600"
}

function formatDate(isoString) {
  if (!isoString) return null
  const date = new Date(isoString)
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function statusBadge(status) {
  const configs = {
    open: { label: "Open", className: "bg-green-100 text-green-800 hover:bg-green-100" },
    won: { label: "Won", className: "bg-blue-100 text-blue-800 hover:bg-blue-100" },
    lost: { label: "Lost", className: "bg-red-100 text-red-800 hover:bg-red-100" },
    abandoned: { label: "Abandoned", className: "bg-zinc-100 text-zinc-600 hover:bg-zinc-100" },
  }
  const config = configs[status] || { label: status || "Unknown", className: "bg-zinc-100 text-zinc-600 hover:bg-zinc-100" }
  return <Badge className={config.className}>{config.label}</Badge>
}

export default function MarketingShow({
  currentUser,
  coach,
  opportunities,
  pipelineStageSummary,
  pagination,
  currentPipeline,
  currentStage,
  pendingCount,
  pipelineNames,
  ghlBaseUrl,
}) {
  const getPipelineColor = (pipelineId) => getPipelineColors(pipelineNames[pipelineId])

  const PIPELINE_FILTERS = [
    { key: null, label: "All Pipelines" },
    ...Object.entries(pipelineNames).map(([id, name]) => ({ key: id, label: name }))
  ]

  const handlePipelineFilter = (pipelineId) => {
    const params = {}
    if (pipelineId) params.pipeline = pipelineId
    // Reset stage filter when pipeline changes
    router.get(`/app/admin/marketing/${coach.id}`, params, {
      preserveState: true,
    })
  }

  const handleStageFilter = (stageId) => {
    const params = {}
    if (currentPipeline) params.pipeline = currentPipeline
    if (stageId) params.stage = stageId
    router.get(`/app/admin/marketing/${coach.id}`, params, {
      preserveState: true,
    })
  }

  const handlePageChange = (newPage) => {
    const params = { page: newPage }
    if (currentPipeline) params.pipeline = currentPipeline
    if (currentStage) params.stage = currentStage
    router.get(`/app/admin/marketing/${coach.id}`, params, {
      preserveState: true,
    })
  }

  // Get stages for the currently selected pipeline
  const currentPipelineStages = currentPipeline && pipelineStageSummary[currentPipeline]
    ? pipelineStageSummary[currentPipeline].stages
    : []

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head
        title={`${coach.name || coach.email} - Marketing - Kingdom Factor Admin`}
      />

      <div className="space-y-6">
        {/* Back Link */}
        <Link
          href="/app/admin/marketing"
          className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Marketing
        </Link>

        {/* Coach Header Card */}
        <Card className="bg-white border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-zinc-900">
                  {coach.name || coach.email}
                </h1>
                <p className="text-sm text-zinc-500 mt-1">{coach.email}</p>
                <div className="flex items-center gap-3 mt-3">
                  {coach.lastSyncedAt ? (
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                      <Clock className="h-3 w-3 mr-1" />
                      Synced {formatDate(coach.lastSyncedAt)}
                    </Badge>
                  ) : (
                    <Badge className="bg-zinc-100 text-zinc-600 hover:bg-zinc-100">
                      Never Synced
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sync Error Banner */}
        {coach.lastSyncError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800">Sync Error</p>
                <p className="text-sm text-red-700 mt-1">
                  {coach.lastSyncError}
                </p>
                {coach.lastSyncErrorAt && (
                  <p className="text-xs text-red-600 mt-1">
                    Occurred {formatDate(coach.lastSyncErrorAt)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Pipeline Stage Summary Cards */}
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
          {Object.entries(pipelineStageSummary).map(
            ([pipelineId, pipeline]) => (
              <Card
                key={pipelineId}
                className={getPipelineColor(pipelineId).card || "border-zinc-200"}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-zinc-900">
                      {pipeline.pipelineName}
                    </CardTitle>
                    {coach.ghlLocationId && (
                      <a
                        href={`${ghlBaseUrl}/v2/location/${coach.ghlLocationId}/opportunities/list`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-zinc-500 hover:text-zinc-700 inline-flex items-center gap-1"
                      >
                        Open in GHL
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <p className="text-2xl font-bold text-zinc-900">
                    {pipeline.totalCount}
                  </p>
                </CardHeader>
                <CardContent className="pt-0">
                  {pipeline.stages.length > 0 ? (
                    <div className="space-y-1.5">
                      {pipeline.stages.map((stage) => (
                        <div
                          key={stage.stageId}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="text-zinc-600 truncate mr-2">
                            {stage.name}
                          </span>
                          <span className="font-medium text-zinc-900 tabular-nums">
                            {stage.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-400">No stages synced</p>
                  )}
                </CardContent>
              </Card>
            )
          )}
        </div>

        {/* Pipeline Filter Tabs */}
        <div className="flex items-center gap-2 flex-wrap">
          {PIPELINE_FILTERS.map((filter) => {
            const isActive =
              currentPipeline === filter.key ||
              (!currentPipeline && !filter.key)
            return (
              <button
                key={filter.label}
                onClick={() => handlePipelineFilter(filter.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-kf-red text-white"
                    : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:border-zinc-300"
                }`}
              >
                {filter.label}
              </button>
            )
          })}
        </div>

        {/* Stage Filter (visible when a pipeline is selected) */}
        {currentPipeline && currentPipelineStages.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-zinc-400 mr-1">Stage:</span>
            <button
              onClick={() => handleStageFilter(null)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                !currentStage
                  ? "bg-zinc-800 text-white"
                  : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
              }`}
            >
              All Stages
            </button>
            {currentPipelineStages.map((stage) => (
              <button
                key={stage.stageId}
                onClick={() => handleStageFilter(stage.stageId)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  currentStage === stage.stageId
                    ? "bg-zinc-800 text-white"
                    : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                }`}
              >
                {stage.name}
                <span className="ml-1 text-zinc-400">({stage.count})</span>
              </button>
            ))}
          </div>
        )}

        {/* Opportunities Table */}
        <Card className="bg-white border-zinc-200">
          <CardContent className="p-0">
            {opportunities.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-200 hover:bg-transparent">
                    <TableHead className="text-zinc-500">Contact</TableHead>
                    <TableHead className="text-zinc-500">Pipeline</TableHead>
                    <TableHead className="text-zinc-500">Stage</TableHead>
                    <TableHead className="text-zinc-500 text-center">
                      Time in Stage
                    </TableHead>
                    <TableHead className="text-zinc-500">Source</TableHead>
                    <TableHead className="text-zinc-500">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {opportunities.map((opp) => {
                    const isStale =
                      opp.timeInStageDays != null && opp.timeInStageDays >= 30
                    return (
                      <TableRow
                        key={opp.id}
                        className={`border-zinc-100 ${
                          isStale
                            ? "bg-red-50/30 hover:bg-red-50/50"
                            : "hover:bg-zinc-50"
                        }`}
                      >
                        <TableCell>
                          {coach.ghlLocationId && opp.ghlContactId ? (
                            <a
                              href={`${ghlBaseUrl}/v2/location/${coach.ghlLocationId}/contacts/detail/${opp.ghlContactId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-kf-red hover:underline font-medium"
                            >
                              {opp.contactName || "Unknown"}
                            </a>
                          ) : (
                            <span className="font-medium text-zinc-900">
                              {opp.contactName || "Unknown"}
                            </span>
                          )}
                          {opp.contactEmail && (
                            <p className="text-xs text-zinc-500 mt-0.5">
                              {opp.contactEmail}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={
                              getPipelineColors(opp.pipelineName).badge
                            }
                          >
                            {opp.pipelineName}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-zinc-600">
                          {opp.stageName}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={timeInStageColor(opp.timeInStageDays)}>
                            {opp.timeInStageDays != null
                              ? `${opp.timeInStageDays}d`
                              : "--"}
                          </span>
                        </TableCell>
                        <TableCell className="text-zinc-600">
                          {opp.source || "--"}
                        </TableCell>
                        <TableCell>{statusBadge(opp.status)}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-zinc-400">
                <TrendingUp className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                <p className="font-medium text-zinc-500">
                  {currentStage
                    ? "No opportunities in this stage"
                    : currentPipeline
                    ? "No opportunities in this pipeline"
                    : "No opportunities synced"}
                </p>
                <p className="text-sm">
                  {currentStage
                    ? "Try selecting a different stage or pipeline filter."
                    : currentPipeline
                    ? "Try selecting a different pipeline filter."
                    : "Opportunities will appear after the next nightly sync."}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-zinc-500">
              {pagination.totalCount} total{" "}
              {pagination.totalCount === 1 ? "opportunity" : "opportunities"}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.currentPage <= 1}
                onClick={() => handlePageChange(pagination.currentPage - 1)}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-zinc-600 px-2">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.currentPage >= pagination.totalPages}
                onClick={() => handlePageChange(pagination.currentPage + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
